import React from 'react';
import { Inventory } from '../types';

type Side = 'white' | 'black';

// Map inventory keys to FEN characters
const PIECE_MAP: Record<string, string> = {
    p: 'P', n: 'N', b: 'B', r: 'R', q: 'Q', k: 'K'
};

const PIECE_ICONS: Record<string, string> = {
    P: '♟', N: '♞', B: '♝', R: '♜', Q: '♛', K: '♚',
    p: '♟', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚'
};

interface TacticalFormationProps {
    inventory: Inventory;
    side: Side;
    blockedSquares: number[];
    setBlockedSquares: (squares: number[]) => void;
}

export const TacticalFormation: React.FC<TacticalFormationProps> = ({ inventory, side, blockedSquares, setBlockedSquares }) => {
    // Define the standard slots for the two rows
    const row1Pieces = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'] as const;

    // Helper to simulate placement based on current blocks
    const getProjectedPiece = (index: number) => {
        // If blocked, empty
        if (blockedSquares.includes(index)) return null;

        // Determine required type
        let requiredType: keyof Inventory = 'p';
        if (index < 8) requiredType = row1Pieces[index];

        // Count how many of this type were used in *previous* unblocked slots
        let usedCount = 0;
        for (let i = 0; i < index; i++) {
            if (blockedSquares.includes(i)) continue;
            const typeAtIndex = i < 8 ? row1Pieces[i] : 'p';
            if (typeAtIndex === requiredType) usedCount++;
        }

        // Check availability
        if (inventory[requiredType] > usedCount) return PIECE_MAP[requiredType];
        return null;
    };

    const toggleBlock = (index: number) => {
        // Cannot block King (Index 4) or Pawns (Indices 8-15)
        if (index === 4 || index >= 8) return;

        if (blockedSquares.includes(index)) {
            setBlockedSquares(blockedSquares.filter(i => i !== index));
        } else {
            setBlockedSquares([...blockedSquares, index]);
        }
    };

    const ORDER_CLASSES: Record<number, string> = {
        2: 'order-1', // B
        3: 'order-2', // Q
        4: 'order-3', // K
        5: 'order-4', // B
        0: 'order-5', // R
        1: 'order-6', // N
        6: 'order-7', // N
        7: 'order-8'  // R
    };

    return (
        <div className="relative bg-tudor-black p-6 w-full shadow-[0_0_50px_rgba(197,160,89,0.2)]">
            <h2 className="text-2xl text-tudor-gold font-serif mb-2 text-center uppercase tracking-widest">Tactical Formation</h2>
            <p className="text-gray-400 text-center text-sm mb-6">Select <span className="text-white font-bold">Elite Pieces</span> to exclude them from battle. Mandates: King & Pawns must deploy.</p>

            <div className="flex flex-col gap-2 bg-[#1a1a1a] p-4 border border-tudor-gray/30 rounded mx-auto w-fit">

                {/* Helper to reverse visual order for Black so it matches board perspective (h..a) */}
                {/* Rank 2 (Pawns - Indices 8-15) - LOCKED */}
                <div className="grid grid-cols-4 gap-2 md:flex md:gap-1">
                    {Array.from({ length: 8 }).map((_, i) => {
                        const offset = side === 'black' ? (7 - i) : i;
                        const idx = 8 + offset;

                        const isBlocked = blockedSquares.includes(idx);
                        //const pieceChar = isBlocked ? 'P' : getProjectedPiece(idx);

                        return (
                            <button
                                key={idx}
                                onClick={() => toggleBlock(idx)}
                                disabled={true}
                                className={`
                                w-12 h-12 flex items-center justify-center border transition-all cursor-not-allowed
                                bg-tudor-gray/5 border-tudor-gold/10 text-gray-500
                            `}
                                title="Pawns cannot be disabled"
                            >
                                <span className="text-3xl font-bold opacity-80">{PIECE_ICONS['p']}</span>
                            </button>
                        )
                    })}
                </div>

                {/* Rank 1 (Elite - Indices 0-7) */}
                <div className="grid grid-cols-4 gap-2 md:flex md:gap-1">
                    {Array.from({ length: 8 }).map((_, i) => {
                        const offset = side === 'black' ? (7 - i) : i;
                        const idx = 0 + offset;

                        const isBlocked = blockedSquares.includes(idx);
                        const isKing = idx === 4;

                        // Determine what *should* be there if blocked/missing
                        const baseType = row1Pieces[idx];
                        const baseChar = PIECE_MAP[baseType];

                        const displayChar = isBlocked ? baseChar : getProjectedPiece(idx);

                        return (
                            <button
                                key={idx}
                                onClick={() => toggleBlock(idx)}
                                disabled={isKing}
                                className={`
                                w-12 h-12 flex items-center justify-center border transition-all relative ${ORDER_CLASSES[idx]} md:order-none
                                ${isKing ? 'border-tudor-gold bg-tudor-gold/10' : ''}
                                ${isBlocked
                                        ? 'bg-red-900/10 border-red-900/30 text-red-800' // Red "Ghost" style
                                        : isKing ? 'text-tudor-gold' : 'bg-tudor-gray/10 border-tudor-gold/30 hover:bg-tudor-gold/20 text-gray-300'}
                            `}
                            >
                                <span className={`text-3xl ${isBlocked ? 'opacity-40 grayscale' : ''}`}>
                                    {displayChar ? PIECE_ICONS[displayChar] : ""}
                                </span>

                                {isBlocked && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div className="w-12 h-[1px] bg-red-600 rotate-45 absolute"></div>
                                    </div>
                                )}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div >
    );
};
